import { instantMs } from '../alarms-api';
import { BandModel, showsWearStatus } from './band-model';

/**
 * Stan opaski pokazywany przy pacjencie — kolorem obwodki awatara i ikona.
 *
 * Trzy stany baterii — 'discharged', 'charging' i 'active' (naladowana / w uzyciu) —
 * musza dawac ten sam kolor co pigulka w app-check-status-user (tam: empty-battery,
 * charging, full-battery), bo stoja obok siebie na tym samym kafelku.
 */
export type BandStatus = 'worn' | 'charging' | 'removed' | 'noactive' | 'active' | 'discharged';

/** Pola pacjenta, ktore wplywaja na stan. Nazwy jak w odpowiedzi backendu. */
export interface BandStatusInput {
  lastMeasurement?: number | string | null;
  is_charge?: boolean;
  /** Czy opaska raportuje, ze jest zalozona. Znaczenie ma tylko dla modeli, ktore to potrafia. */
  status?: boolean;
  batteryLevel?: number;
}

export function isContactStale(lastMeasurement: number | string | null | undefined, timeoutHours: number, now: number): boolean {
  if (lastMeasurement === undefined) {
    return false;
  }
  const contactMs = instantMs(lastMeasurement);
  return contactMs === null || now - contactMs > timeoutHours * 3600000;
}

/** Ten sam warunek baterii, ktory stawia filtr aktywnosci listy na serwerze (BandActivity::ACTIVE_SQL). */
export function hasActiveBattery(batteryLevel: number): boolean {
  return batteryLevel >= 1 && batteryLevel <= 100;
}

/**
 * Do tego poziomu WLACZNIE opaska jest "rozladowana" (0-15%). Jeden prog dla pigulki
 * w app-check-status-user, obwodki awatara (bandStatusOf) i wykrzyknika na ikonie
 * baterii (app-battery-level) - wczesniej kazde z nich sprawdzalo samo 0%, a opaska
 * na 10% udawala zdrowa, choc firmware juz wysylal "Band discharged".
 */
export const DISCHARGED_BATTERY_LEVEL = 15;

/**
 * Tylko znany odczyt: brak odczytu (undefined/null) to "nieaktywna", bo bateria moze byc
 * w porzadku. Opaska na ladowarce nie jest rozladowana - piorun/"Na ladowarce" mowi wiecej.
 */
export function isDischargedBattery(band: BandStatusInput): boolean {
  return !!band && typeof band.batteryLevel === 'number' && band.batteryLevel <= DISCHARGED_BATTERY_LEVEL && !band.is_charge;
}

/**
 * Stan opaski. Wczesniej ta sama logika stala w CZTERECH kopiach — w dashboardzie,
 * karcie pacjenta i obu ekranach konfiguracji — i wszystkie cztery trzeba bylo poprawic
 * naraz, gdy doszedl Easy Care. Kopie roznily sie przy tym drobiazgami: dashboard nie
 * mial oslony na brak pacjenta.
 *
 * `timeoutHours` przychodzi z ustawien (CaregiverService), a nie jest tu zaszyty na sztywno —
 * operator moze zmienic prog nieaktywnosci, a tlo ikonki ma sie zgadzac z app-check-status-user.
 * `now` jest parametrem, zeby test nie zalezal od zegara.
 */
export function bandStatusOf(model: BandModel, band: BandStatusInput, timeoutHours: number, now: number = Date.now()): BandStatus {
  if (!band) {
    return 'worn';
  }

  if (isContactStale(band.lastMeasurement, timeoutHours, now)) {
    return 'noactive';
  }

  // Rozladowana (0-15%) to osobny stan (czerwony), NIE ogolne "nieaktywna" (szary):
  // app-check-status-user podpisuje taka opaske "Rozladowana" i awatar ma sie z tym zgadzac.
  if (isDischargedBattery(band)) {
    return 'discharged';
  }

  // is_charge nie gasnie po dojsciu do 100% (patrz startCharge w socket.service.ts), wiec pelna
  // opaska lezaca na ladowarce to "naladowana" (zielony), nie "laduje sie" (pomaranczowy) —
  // ta sama regula, ktora app-check-status-user pokazuje jako "Naladowana".
  // Przed sprawdzeniem baterii, bo opaska na ladowarce z 0% laduje sie, a nie jest nieaktywna.
  if (band.is_charge) {
    return band.batteryLevel >= 100 ? 'active' : 'charging';
  }

  if (!hasActiveBattery(band.batteryLevel)) {
    return 'noactive';
  }

  if (!showsWearStatus(model)) {
    return 'active';
  }

  // Vital trzeba wymieniac jawnie: dopoki czytalismy surowe type_hardware, wychodzil
  // na to ubocznie, bo RevJ ma tam wartosc CARE.
  if (model === 'CARE_PRO' || model === 'VITAL_CARE') {
    return band.status ? 'worn' : 'removed';
  }

  return 'worn';
}
